// src/pages/ImportExport.jsx
// CSV Export + Import (ODFTrades format + MT4/MT5)
import React, { useState, useRef, useCallback } from 'react';
import { Download, Upload, FileText, AlertTriangle, CheckCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { exportTradesToCSV, getCSVTemplate } from '../utils/csvExport';
import {
  parseCSV, detectFormat, mapODFRow, mapMT4Row,
  validateTrade, deduplicateTrades, buildColumnMapper
} from '../utils/csvImport';

const G = '#00e5a0';

function Tab({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 20px', borderRadius: 8, border: '1px solid',
      fontSize: 13, cursor: 'pointer', outline: 'none',
      fontFamily: "'IBM Plex Sans', sans-serif",
      background: active ? 'rgba(0,229,160,0.1)' : 'transparent',
      borderColor: active ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
      color: active ? G : '#8888a8',
      fontWeight: active ? 600 : 400,
      transition: 'all 0.15s',
    }}>{children}</button>
  );
}

export default function ImportExport({ trades, settings, onImport , lang = 'en'}) {
  const [tab,         setTab]         = useState('export');
  const [importing,   setImporting]   = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [preview,     setPreview]     = useState(null); // { trades, format, errors, skipped }
  const [mapMode,     setMapMode]     = useState(false);
  const [columnMap,   setColumnMap]   = useState({});
  const [rawHeaders,  setRawHeaders]  = useState([]);
  const [rawRows,     setRawRows]     = useState([]);
  const [importResult,setImportResult]= useState(null);
  const [dragOver,    setDragOver]    = useState(false);
  const fileRef = useRef(null);

  // ── EXPORT ────────────────────────────────────────────────────
  const handleExport = () => {
    setExporting(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      exportTradesToCSV(trades, settings, `odftrades-${date}.csv`);
    } catch (e) {
      console.error('Export failed:', e);
    }
    setTimeout(() => setExporting(false), 1000);
  };

  const handleTemplate = () => {
    getCSVTemplate();
  };

  // ── FILE HANDLING ─────────────────────────────────────────────
  const processFile = useCallback((file) => {
    if (!file || !file.name.endsWith('.csv')) {
      alert('Please upload a .csv file');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      const { headers, rows } = parseCSV(text);

      if (!headers.length || !rows.length) {
        alert('CSV file appears to be empty or invalid.');
        return;
      }

      const format = detectFormat(headers);
      setRawHeaders(headers);
      setRawRows(rows);

      if (format === 'odftrades') {
        processODFRows(rows, trades);
      } else if (format === 'mt4') {
        processMT4Rows(rows, trades);
      } else {
        // Unknown format — show column mapper
        const { fields, suggestions } = buildColumnMapper(headers);
        setColumnMap(suggestions);
        setMapMode(true);
        setPreview({ trades: [], format: 'unknown', errors: [], skipped: [], fields, headers });
      }
    };
    reader.readAsText(file);
  }, [trades]);

  const processODFRows = (rows, existingTrades) => {
    const mapped  = rows.map(mapODFRow);
    const allErrors = [];
    mapped.forEach((t, i) => {
      const errs = validateTrade(t, i);
      allErrors.push(...errs);
    });
    const { unique, skipped } = deduplicateTrades(mapped, existingTrades);
    setPreview({ trades: unique, format: 'odftrades', errors: allErrors, skipped });
    setMapMode(false);
  };

  const processMT4Rows = (rows, existingTrades) => {
    const mapped  = rows.map(mapMT4Row);
    const allErrors = [];
    mapped.forEach((t, i) => {
      const errs = validateTrade(t, i);
      allErrors.push(...errs);
    });
    const { unique, skipped } = deduplicateTrades(mapped, existingTrades);
    setPreview({ trades: unique, format: 'mt4', errors: allErrors, skipped });
    setMapMode(false);
  };

  const processCustomMap = () => {
    // Apply custom column mapping
    const mapped = rawRows.map(row => {
      const trade = {};
      for (const [tradeKey, csvHeader] of Object.entries(columnMap)) {
        if (csvHeader && row[csvHeader] !== undefined) {
          trade[tradeKey] = row[csvHeader];
        }
      }
      return mapODFRow(trade);
    });
    const allErrors = [];
    mapped.forEach((t, i) => { allErrors.push(...validateTrade(t, i)); });
    const { unique, skipped } = deduplicateTrades(mapped, trades);
    setPreview({ trades: unique, format: 'custom', errors: allErrors, skipped });
    setMapMode(false);
  };

  // ── CONFIRM IMPORT ────────────────────────────────────────────
  const handleConfirmImport = async () => {
    if (!preview?.trades?.length) return;
    setImporting(true);
    try {
      const validTrades = preview.trades.filter(t =>
        t.date && t.pair && ['Win','Loss','Breakeven'].includes(t.result)
      );
      let imported = 0;
      let failed = 0;
      for (const trade of validTrades) {
        try {
          await onImport(trade);
          imported++;
        } catch (e) {
          failed++;
        }
      }
      setImportResult({ imported, failed, skipped: preview.skipped?.length || 0 });
      setPreview(null);
    } catch (e) {
      console.error('Import failed:', e);
    }
    setImporting(false);
  };

  const inp = { background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, color:'#f0f0f5', padding:'7px 10px', fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif", outline:'none', width:'100%', boxSizing:'border-box' };

  return (
    <div style={{ padding:'28px 32px', maxWidth:960, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:600, color:'#f0f0f5', margin:0, fontFamily:"'IBM Plex Sans',sans-serif" }}>Import / Export</h1>
        <p style={{ color:'#55556a', fontSize:13, margin:'4px 0 0', fontFamily:"'DM Mono',monospace" }}>
          Export your trades to CSV · Import from ODFTrades or MetaTrader
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        <Tab active={tab==='export'} onClick={() => { setTab('export'); setPreview(null); setImportResult(null); }}>
          <Download size={14} style={{ marginRight:6, verticalAlign:'middle' }} />Export
        </Tab>
        <Tab active={tab==='import'} onClick={() => { setTab('import'); setPreview(null); setImportResult(null); }}>
          <Upload size={14} style={{ marginRight:6, verticalAlign:'middle' }} />Import
        </Tab>
      </div>

      {/* ── EXPORT TAB ── */}
      {tab === 'export' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Main export card */}
          <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:28 }}>
            <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', marginBottom:6 }}>Export All Trades</div>
            <div style={{ fontSize:13, color:'#8888a8', marginBottom:20, lineHeight:1.7 }}>
              Download all your logged trades as a CSV file. Includes all fields: date, pair, P&L, RR, risk, emotions, mistakes, notes, and more.
              The export is compatible with Excel, Google Sheets, and re-importing back into ODFTrades.
            </div>

            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:20 }}>
              <div style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'12px 18px' }}>
                <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Trades</div>
                <div style={{ fontSize:22, fontWeight:700, color:G, fontFamily:"'DM Mono',monospace" }}>{trades.length}</div>
              </div>
              <div style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'12px 18px' }}>
                <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Format</div>
                <div style={{ fontSize:16, fontWeight:600, color:'#f0f0f5', fontFamily:"'DM Mono',monospace" }}>CSV</div>
              </div>
              <div style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:10, padding:'12px 18px' }}>
                <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Fields</div>
                <div style={{ fontSize:16, fontWeight:600, color:'#f0f0f5', fontFamily:"'DM Mono',monospace" }}>22</div>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <button onClick={handleExport} disabled={exporting || trades.length === 0} style={{
                display:'flex', alignItems:'center', gap:8,
                background: trades.length === 0 ? 'rgba(0,229,160,0.3)' : G,
                color:'#0a0a0f', border:'none', borderRadius:9, padding:'12px 24px',
                fontSize:14, fontWeight:700, cursor: trades.length === 0 ? 'not-allowed' : 'pointer',
                fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s',
                opacity: trades.length === 0 ? 0.5 : 1,
              }}>
                <Download size={16} />
                {exporting ? 'Downloading...' : `Export ${trades.length} Trade${trades.length !== 1 ? 's' : ''}`}
              </button>
              <button onClick={handleTemplate} style={{
                display:'flex', alignItems:'center', gap:8,
                background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e',
                borderRadius:9, padding:'12px 20px', fontSize:13, cursor:'pointer',
                fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2a2a3e'; e.currentTarget.style.color = '#f0f0f5'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e2e'; e.currentTarget.style.color = '#8888a8'; }}>
                <FileText size={14} /> Download Import Template
              </button>
            </div>

            {trades.length === 0 && (
              <div style={{ marginTop:16, fontSize:12, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
                Log trades first before exporting.
              </div>
            )}
          </div>

          {/* Fields reference */}
          <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:24 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:14 }}>Export Fields</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {['Date','Pair','Session','Direction','Entry','Stop Loss','Take Profit','Risk $','Risk %','Lot Size','Contracts','Result','P&L $','P&L %','RR','Rules','Strategy','Order Flow','Timeframe','Mistake','Emotion Before','Emotion After','Notes','Screenshot'].map(f => (
                <span key={f} style={{ fontSize:11, color:'#8888a8', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:5, padding:'3px 9px', fontFamily:"'DM Mono',monospace" }}>{f}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT TAB ── */}
      {tab === 'import' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Import result */}
          {importResult && (
            <div style={{ background:'rgba(0,229,160,0.06)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:12, padding:20, display:'flex', alignItems:'center', gap:16 }}>
              <CheckCircle size={24} style={{ color:G, flexShrink:0 }} />
              <div>
                <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5', marginBottom:4 }}>Import complete</div>
                <div style={{ fontSize:13, color:'#8888a8' }}>
                  {importResult.imported} trade{importResult.imported !== 1 ? 's' : ''} imported
                  {importResult.skipped > 0 && ` · ${importResult.skipped} duplicates skipped`}
                  {importResult.failed > 0 && ` · ${importResult.failed} failed`}
                </div>
              </div>
              <button onClick={() => setImportResult(null)} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#55556a' }}>
                <X size={18} />
              </button>
            </div>
          )}

          {/* Drop zone */}
          {!preview && !mapMode && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              style={{
                background: dragOver ? 'rgba(0,229,160,0.08)' : '#16161f',
                border: `2px dashed ${dragOver ? 'rgba(0,229,160,0.4)' : '#2a2a3e'}`,
                borderRadius:14, padding:48, textAlign:'center', cursor:'pointer',
                transition:'all 0.2s',
              }}>
              <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e => processFile(e.target.files[0])} />
              <Upload size={32} style={{ color:dragOver?G:'#2a2a3e', marginBottom:16 }} />
              <div style={{ fontSize:15, fontWeight:600, color:dragOver?G:'#f0f0f5', marginBottom:8 }}>
                Drop your CSV file here
              </div>
              <div style={{ fontSize:13, color:'#55556a', marginBottom:16 }}>or click to browse</div>
              <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                {['ODFTrades CSV', 'MT4 History', 'MT5 History', 'Custom CSV'].map(f => (
                  <span key={f} style={{ fontSize:11, color:'#55556a', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:5, padding:'3px 9px', fontFamily:"'DM Mono',monospace" }}>{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Column mapper for unknown formats */}
          {mapMode && preview && (
            <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:24 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', marginBottom:6 }}>Map Your Columns</div>
              <div style={{ fontSize:13, color:'#8888a8', marginBottom:20, lineHeight:1.6 }}>
                We couldn't automatically detect your CSV format. Match your column headers to the ODFTrades fields below.
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:12, marginBottom:20 }}>
                {preview.fields?.map(field => (
                  <div key={field.key}>
                    <label style={{ fontSize:11, color: field.required ? G : '#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:5 }}>
                      {field.label}{field.required && ' *'}
                    </label>
                    <select value={columnMap[field.key] || ''} onChange={e => setColumnMap(m => ({ ...m, [field.key]: e.target.value }))} style={inp}>
                      <option value="">— Not mapped —</option>
                      {preview.headers?.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={processCustomMap} style={{ background:G, color:'#0a0a0f', border:'none', borderRadius:8, padding:'11px 24px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
                  Preview Import →
                </button>
                <button onClick={() => { setMapMode(false); setPreview(null); }} style={{ background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:8, padding:'11px 18px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview && !mapMode && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

              {/* Summary bar */}
              <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:3 }}>Format detected</div>
                  <div style={{ fontSize:13, fontWeight:600, color: preview.format==='mt4'?'#4cc9f0': preview.format==='odftrades'?G:'#ffd166' }}>
                    {preview.format === 'mt4' ? 'MT4 / MT5 History' : preview.format === 'odftrades' ? 'ODFTrades CSV' : 'Custom (mapped)'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:3 }}>Ready to import</div>
                  <div style={{ fontSize:22, fontWeight:700, color:G, fontFamily:"'DM Mono',monospace" }}>{preview.trades.length}</div>
                </div>
                {preview.skipped?.length > 0 && (
                  <div>
                    <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:3 }}>Duplicates skipped</div>
                    <div style={{ fontSize:22, fontWeight:700, color:'#ffd166', fontFamily:"'DM Mono',monospace" }}>{preview.skipped.length}</div>
                  </div>
                )}
                {preview.errors?.length > 0 && (
                  <div>
                    <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:3 }}>Validation issues</div>
                    <div style={{ fontSize:22, fontWeight:700, color:'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>{preview.errors.length}</div>
                  </div>
                )}
              </div>

              {/* Errors */}
              {preview.errors?.length > 0 && (
                <div style={{ background:'rgba(255,77,109,0.06)', border:'1px solid rgba(255,77,109,0.2)', borderRadius:10, padding:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                    <AlertTriangle size={16} style={{ color:'#ff4d6d' }} />
                    <span style={{ fontSize:13, fontWeight:600, color:'#ff4d6d' }}>Validation issues (these rows will be skipped)</span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:120, overflowY:'auto' }}>
                    {preview.errors.map((e, i) => (
                      <div key={i} style={{ fontSize:12, color:'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>{e}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trade preview table */}
              {preview.trades.length > 0 && (
                <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:12, overflow:'hidden' }}>
                  <div style={{ padding:'14px 18px', borderBottom:'1px solid #1e1e2e', fontSize:13, fontWeight:600, color:'#f0f0f5' }}>
                    Preview — first 5 trades
                  </div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead>
                        <tr style={{ background:'#0d0d14' }}>
                          {['Date','Pair','Direction','Result','P&L','RR'].map(h => (
                            <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#55556a', fontFamily:"'DM Mono',monospace", fontSize:10, textTransform:'uppercase', letterSpacing:'0.08em', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.trades.slice(0, 5).map((t, i) => (
                          <tr key={i} style={{ borderTop:'1px solid #1e1e2e' }}>
                            <td style={{ padding:'8px 12px', color:'#8888a8', fontFamily:"'DM Mono',monospace", whiteSpace:'nowrap' }}>{t.date}</td>
                            <td style={{ padding:'8px 12px', color:'#f0f0f5', fontWeight:500 }}>{t.pair}</td>
                            <td style={{ padding:'8px 12px', color: t.direction==='Buy'?G:'#ff4d6d' }}>{t.direction}</td>
                            <td style={{ padding:'8px 12px', color: t.result==='Win'?G:t.result==='Loss'?'#ff4d6d':'#ffd166', fontFamily:"'DM Mono',monospace", fontSize:11 }}>{t.result}</td>
                            <td style={{ padding:'8px 12px', color: Number(t.pnl||0)>=0?G:'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>
                              {Number(t.pnl||0)>=0?'+':''}${Number(t.pnl||0).toFixed(2)}
                            </td>
                            <td style={{ padding:'8px 12px', color:'#8888a8', fontFamily:"'DM Mono',monospace" }}>{t.rrAchieved||'—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.trades.length > 5 && (
                      <div style={{ padding:'10px 18px', fontSize:12, color:'#55556a', fontFamily:"'DM Mono',monospace", borderTop:'1px solid #1e1e2e' }}>
                        +{preview.trades.length - 5} more trades
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {preview.trades.length > 0 && (
                  <button onClick={handleConfirmImport} disabled={importing} style={{
                    display:'flex', alignItems:'center', gap:8,
                    background:G, color:'#0a0a0f', border:'none', borderRadius:9,
                    padding:'12px 24px', fontSize:14, fontWeight:700,
                    cursor:importing?'not-allowed':'pointer',
                    fontFamily:"'IBM Plex Sans',sans-serif",
                    opacity:importing?0.7:1, transition:'all 0.2s',
                  }}>
                    {importing ? (
                      <><span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(0,0,0,0.3)', borderTopColor:'#0a0a0f', display:'inline-block', animation:'spin 0.7s linear infinite' }} /> Importing...</>
                    ) : (
                      <><CheckCircle size={16} /> Confirm Import ({preview.trades.length} trades)</>
                    )}
                  </button>
                )}
                <button onClick={() => { setPreview(null); setMapMode(false); if (fileRef.current) fileRef.current.value = ''; }} style={{
                  background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e',
                  borderRadius:9, padding:'12px 18px', fontSize:13, cursor:'pointer',
                  fontFamily:"'IBM Plex Sans',sans-serif",
                }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Format guide */}
          {!preview && !mapMode && (
            <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:24 }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:16 }}>Supported Formats</div>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {[
                  { name:'ODFTrades CSV', desc:'Exported directly from ODFTrades. All fields mapped automatically.', color:G, badge:'Recommended' },
                  { name:'MT4 History Export', desc:'Export from MetaTrader 4: Account History tab → Save as Report (CSV). Fields mapped automatically.', color:'#4cc9f0', badge:'MT4/MT5' },
                  { name:'MT5 History Export', desc:'Export from MetaTrader 5: History tab → All History → Save as Detailed Report.', color:'#4cc9f0', badge:'MT4/MT5' },
                  { name:'Custom CSV', desc:'Any CSV file. Use the column mapper to match your headers to ODFTrades fields.', color:'#ffd166', badge:'Manual map' },
                ].map((f, i) => (
                  <div key={i} style={{ display:'flex', gap:14, alignItems:'flex-start', padding:'12px 0', borderBottom: i < 3 ? '1px solid #1a1a26' : 'none' }}>
                    <div style={{ background:`${f.color}15`, border:`1px solid ${f.color}30`, borderRadius:6, padding:'3px 10px', fontSize:10, color:f.color, fontFamily:"'DM Mono',monospace", whiteSpace:'nowrap', marginTop:1 }}>{f.badge}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', marginBottom:3 }}>{f.name}</div>
                      <div style={{ fontSize:12, color:'#55556a', lineHeight:1.6 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #1e1e2e' }}>
                <button onClick={handleTemplate} style={{ display:'flex', alignItems:'center', gap:7, background:'transparent', color:G, border:`1px solid rgba(0,229,160,0.3)`, borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600 }}>
                  <Download size={13} /> Download ODFTrades Import Template
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
